import React, { useState, useEffect, useRef } from 'react';
import { useUser, RedirectToSignIn } from '@clerk/react';
import { useSubscription } from '@clerk/react/experimental';
import { generateBalancedSubset, combinations } from './optimizer';
//import Header from "./Header";  Removed Header import from Analyzer since it's now in App.js
import KenoBoard from "./KenoBoard";
import CardList from "./CardList";
import LeftPanel from "./LeftPanel";
import RightPanel from "./RightPanel";
import SessionPanel from "./SessionPanel";
import PayTableModal from "./PayTableModal";
import DenominationModal from "./DenominationModal";
import PaytableSelector from "./PaytableSelector";
import AnalysisPanel from "./AnalysisPanel";
import AnalysisSummary from "./AnalysisSummary";
import SaveConfigModal from "./SaveConfigModal";
import LoadConfigModal from "./LoadConfigModal";
import RenameConfigModal from "./RenameConfigModal";
import './App.css';


function Analyzer({
  activeCard,
  setActiveCard,
  cardSelections,
  setCardSelections,
  bets,
  setBets,
  showAllCards,
  setShowAllCards,
  drawnNumbers,
  setDrawnNumbers,
  hits,
  setHits,
  payouts,
  setPayouts,
  hitFrequencies,
  setHitFrequencies,
  sessionGamesPlayed,
  setSessionGamesPlayed,
  sessionTotalWon,
  setSessionTotalWon,
  sessionTotalWagered,
  setSessionTotalWagered,
  credit,
  setCredit,
  denomination,
  setDenomination,
  activePaytable,
  setActivePaytable,
  speed,
  setSpeed,
  savedConfigurations,
  setSavedConfigurations,
  isAutoSession,
  setIsAutoSession,
  autoSessionRemaining,
  setAutoSessionRemaining,
  autoSessionTotal,
  setAutoSessionTotal,
  showContinueButton,
  setShowContinueButton,
    allowBackgroundSessions,
    setAllowBackgroundSessions,
    autoSessionIdRef
}) {

const { isLoaded, isSignedIn } = useUser();
const { data: subscriptionData } = useSubscription();
const isPremium = subscriptionData?.subscriptionItems?.some(
  item => !item.plan.isDefault && item.status === 'active'
) ?? false;

const FREE_CREDIT_CAP = 100;
const FREE_GAME_LIMIT = 25;
const FREE_CONFIG_LIMIT = 1;
const [showCreditCapMsg, setShowCreditCapMsg] = useState(false);
const [showSessionLimitModal, setShowSessionLimitModal] = useState(false);
const [showConfigLimitModal, setShowConfigLimitModal] = useState(false);
const creditCapMsgTimer = useRef(null);
const pendingContinueAction = useRef(null);

const atSessionLimit = !isPremium && sessionGamesPlayed >= FREE_GAME_LIMIT;

// When subscription lapses back to free, cap credits and disable background sessions
useEffect(() => {
  if (!isPremium && credit > FREE_CREDIT_CAP) {
    setCredit(FREE_CREDIT_CAP);
  }
  if (!isPremium) {
    setAllowBackgroundSessions(false);
  }
}, [isPremium]); // eslint-disable-line react-hooks/exhaustive-deps

const handleSaveConfigClick = () => {
  if (!isPremium && savedConfigurations.length >= FREE_CONFIG_LIMIT) {
    setShowConfigLimitModal(true);
  } else {
    setShowSaveConfigModal(true);
  }
};

const handleContinueSession = () => {
  const action = pendingContinueAction.current;
  pendingContinueAction.current = null;
  setShowSessionLimitModal(false);
  // Full stats reset for both paths
  setSessionGamesPlayed(0);
  setSessionTotalWon(0);
  setSessionTotalWagered(0);
  setHitFrequencies(
    cards.reduce((acc, card) => {
      acc[card] = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, totalWon: 0 };
      return acc;
    }, {})
  );
  setIsAutoSession(false);
  setAutoSessionRemaining(0);
  setShowContinueButton(false);
  if (action === 'preAutoSession') {
    // Open auto session modal — user came from New Auto Session button
    setShowAutoSessionModal(true);
  }
  // For 'manual' (single Start button), modal just closes — user presses Start again
};

const handleAddCredit = () => {
  if (isPremium) {
    setCredit(prev => prev + 100);
  } else {
    if (credit < FREE_CREDIT_CAP) {
      setCredit(FREE_CREDIT_CAP);
    } else {
      setShowCreditCapMsg(true);
      clearTimeout(creditCapMsgTimer.current);
      creditCapMsgTimer.current = setTimeout(() => setShowCreditCapMsg(false), 4000);
    }
  }
};

const cards = Array.from({ length: 20 }, (_, i) => String.fromCharCode(65 + i));

// Optimizer mode state
const [optimizerMode, setOptimizerMode] = useState(false);
const [optimizerNumbers, setOptimizerNumbers] = useState([]);
const [optimizerSpots, setOptimizerSpots] = useState(10);
const [optimizerCardCount, setOptimizerCardCount] = useState(20);
const [canShuffle, setCanShuffle] = useState(false);
const [optimizerStatusMsg, setOptimizerStatusMsg] = useState('');
const [optimizerGridLocked, setOptimizerGridLocked] = useState(false);
const [toastMsg, setToastMsg] = useState('');
const [toastVisible, setToastVisible] = useState(false);
const toastTimerRef = useRef(null);

const showToast = (msg) => {
  if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  setToastMsg(msg);
  setToastVisible(true);
  toastTimerRef.current = setTimeout(() => setToastVisible(false), 2000);
};

const nCr = (n, k) => {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let result = 1;
  for (let i = 0; i < k; i++) {
    result = result * (n - i) / (i + 1);
  }
  return Math.round(result);
};

const handleOptimizerNumberToggle = (num) => {
  if (optimizerGridLocked) return;
  setOptimizerNumbers((prev) => {
    if (prev.includes(num)) return prev.filter((n) => n !== num);
    if (prev.length >= 20) return prev;
    return [...prev, num];
  });
};

const handleRunOptimizer = () => {
  const optimizedCards = generateBalancedSubset(
    optimizerNumbers,
    optimizerSpots,
    optimizerCardCount
  );
  setCanShuffle(true);
  setOptimizerGridLocked(true);
  // Map optimizedCards to cardSelections
  const newSelections = {};
  for (let i = 0; i < cards.length; i++) {
    newSelections[cards[i]] = optimizedCards[i] || [];
  }
  setCardSelections(newSelections);
  setShowAllCards(true);
  // Calculate spread and message
  const counts = {};
  optimizerNumbers.forEach(n => { counts[n] = 0; });
  optimizedCards.forEach(sel => sel.forEach(n => { counts[n]++; }));
  const vals = Object.values(counts);
  const spread = Math.max(...vals) - Math.min(...vals);
  setOptimizerStatusMsg(`${optimizedCards.length} cards generated. Spread = ${spread}. Each number used ${Math.min(...vals)} to ${Math.max(...vals)} times.`);
  showToast(`${optimizedCards.length} cards generated. Spread = ${spread}. Each number used ${Math.min(...vals)} to ${Math.max(...vals)} times.`);
  setOptimizerMode(false);
};


const handleShuffleActiveCard = () => {
  if (!canShuffle || !activeCard || showAllCards) return;
  setOptimizerGridLocked(true);
  // Get all current selections except the active card
  const currentSelections = Object.entries(cardSelections)
    .filter(([card]) => card !== activeCard)
    .map(([, sel]) => sel);
  const usedSigs = new Set(currentSelections.map(sel => sel.slice().sort((a,b)=>a-b).join(',')));

  // Generate all possible combinations for the optimizerNumbers and optimizerSpots
  const allCombos = combinations(optimizerNumbers, optimizerSpots);
  // Exclude those already used and the current selection for the active card
  const currentActiveSig = (cardSelections[activeCard] || []).slice().sort((a,b)=>a-b).join(',');
  const available = allCombos.filter(sel => {
    const sig = sel.slice().sort((a,b)=>a-b).join(',');
    return !usedSigs.has(sig) && sig !== currentActiveSig;
  });
  if (available.length === 0) {
    // If no other unique combos, allow any except current
    const fallback = allCombos.filter(sel => {
      const sig = sel.slice().sort((a,b)=>a-b).join(',');
      return sig !== currentActiveSig;
    });
    if (fallback.length === 0) return;
    // Pick randomly
    const newSelection = fallback[Math.floor(Math.random() * fallback.length)];
    setCardSelections(prev => ({
      ...prev,
      [activeCard]: newSelection
    }));
    // Calculate spread and message
    const getCounts = (selections) => {
      const counts = {};
      optimizerNumbers.forEach(n => { counts[n] = 0; });
      selections.forEach(sel => sel.forEach(n => { counts[n]++; }));
      return counts;
    };
    const counts = getCounts([...currentSelections, newSelection]);
    const vals = Object.values(counts);
    const spread = Math.max(...vals) - Math.min(...vals);
    setOptimizerStatusMsg(`Card ${activeCard} shuffled. Spread = ${spread}. Each number used ${Math.min(...vals)} to ${Math.max(...vals)} times.`);
    return;
  }

  // Helper to count number usage
  const getCounts = (selections) => {
    const counts = {};
    optimizerNumbers.forEach(n => { counts[n] = 0; });
    selections.forEach(sel => sel.forEach(n => { counts[n]++; }));
    return counts;
  };

  // Find the candidate that results in the most balanced distribution
  let bestSpread = Infinity;
  let bestCandidates = [];
  for (const candidate of available) {
    const newSelections = [...currentSelections, candidate];
    const counts = getCounts(newSelections);
    const vals = Object.values(counts);
    const spread = Math.max(...vals) - Math.min(...vals);
    if (spread < bestSpread) {
      bestSpread = spread;
      bestCandidates = [candidate];
    } else if (spread === bestSpread) {
      bestCandidates.push(candidate);
    }
  }
  // Pick randomly among best candidates
  const newSelection = bestCandidates[Math.floor(Math.random() * bestCandidates.length)];
  setCardSelections(prev => ({
    ...prev,
    [activeCard]: newSelection
  }));
  // Calculate spread and message
  const counts = getCounts([...currentSelections, newSelection]);
  const vals = Object.values(counts);
  const spread = Math.max(...vals) - Math.min(...vals);
  setOptimizerStatusMsg(`Card ${activeCard} shuffled. Spread = ${spread}. Each number used ${Math.min(...vals)} to ${Math.max(...vals)} times.`);
};
  const isGameRunningRef = useRef(false);
  const creditCheckRef = useRef(false);
  const creditRef = useRef(100);

  // Default paytable structure
  const defaultPaytables = {
    '838': {
      '1': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '2': { 0: 0, 1: 0, 2: 16, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '3': { 0: 0, 1: 0, 2: 2, 3: 48, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '4': { 0: 0, 1: 0, 2: 2, 3: 5, 4: 100, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '5': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 13, 5: 838, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '6': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 4, 5: 75, 6: 1660, 7: 0, 8: 0, 9: 0, 10: 0 },
      '7': { 0: 0, 1: 0, 2: 0, 3: 1, 4: 2, 5: 22, 6: 422, 7: 7000, 8: 0, 9: 0, 10: 0 },
      '8': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 2, 5: 13, 6: 100, 7: 1670, 8: 10000, 9: 0, 10: 0 },
      '9': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 1, 5: 6, 6: 44, 7: 362, 8: 4700, 9: 10000, 10: 0 },
      '10': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 5, 6: 24, 7: 146, 8: 1000, 9: 4500, 10: 10000 }
    },
    '810': {
      '1': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '2': { 0: 0, 1: 0, 2: 15, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '3': { 0: 0, 1: 0, 2: 2, 3: 46, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '4': { 0: 0, 1: 0, 2: 2, 3: 5, 4: 91, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '5': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 12, 5: 810, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '6': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 4, 5: 70, 6: 1600, 7: 0, 8: 0, 9: 0, 10: 0 },
      '7': { 0: 0, 1: 0, 2: 0, 3: 1, 4: 2, 5: 21, 6: 400, 7: 7000, 8: 0, 9: 0, 10: 0 },
      '8': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 2, 5: 12, 6: 98, 7: 1652, 8: 10000, 9: 0, 10: 0 },
      '9': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 1, 5: 6, 6: 44, 7: 335, 8: 4700, 9: 10000, 10: 0 },
      '10': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 5, 6: 24, 7: 142, 8: 1000, 9: 4500, 10: 10000 }
    },
    '750': {
      '1': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '2': { 0: 0, 1: 0, 2: 15, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '3': { 0: 0, 1: 0, 2: 2, 3: 46, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '4': { 0: 0, 1: 0, 2: 2, 3: 5, 4: 91, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '5': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 11, 5: 750, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '6': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 4, 5: 55, 6: 1500, 7: 0, 8: 0, 9: 0, 10: 0 },
      '7': { 0: 0, 1: 0, 2: 0, 3: 1, 4: 2, 5: 21, 6: 335, 7: 7000, 8: 0, 9: 0, 10: 0 },
      '8': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 2, 5: 12, 6: 98, 7: 1652, 8: 10000, 9: 0, 10: 0 },
      '9': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 1, 5: 6, 6: 44, 7: 335, 8: 4700, 9: 10000, 10: 0 },
      '10': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 5, 6: 24, 7: 142, 8: 1000, 9: 4500, 10: 10000 }
    },
    '804': {
      '1': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '2': { 0: 0, 1: 0, 2: 14, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '3': { 0: 0, 1: 0, 2: 2, 3: 45, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '4': { 0: 0, 1: 0, 2: 2, 3: 3, 4: 85, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '5': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 11, 5: 804, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 },
      '6': { 0: 0, 1: 0, 2: 0, 3: 3, 4: 4, 5: 68, 6: 1500, 7: 0, 8: 0, 9: 0, 10: 0 },
      '7': { 0: 0, 1: 0, 2: 0, 3: 1, 4: 2, 5: 20, 6: 390, 7: 7000, 8: 0, 9: 0, 10: 0 },
      '8': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 2, 5: 12, 6: 98, 7: 1550, 8: 10000, 9: 0, 10: 0 },
      '9': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 1, 5: 6, 6: 44, 7: 300, 8: 4700, 9: 10000, 10: 0 },
      '10': { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 5, 6: 23, 7: 132, 8: 1000, 9: 4500, 10: 10000 }
    }
  };

  // Load initial state from localStorage or use defaults
  const loadFromStorage = (key, defaultValue) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  };

  // Spot size selection for analysis
  const [selectedSpotSize, setSelectedSpotSize] = useState(null);
  const [isGameRunning, setIsGameRunning] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  const [betCursor, setBetCursor] = useState(0);
  const [betSequenceStarted, setBetSequenceStarted] = useState(false);
  const [showDenomModal, setShowDenomModal] = useState(false);
  const [totalWin, setTotalWin] = useState(0);
  const [showPaytableModal, setShowPaytableModal] = useState(false);
  const [isCreatingNewPaytable, setIsCreatingNewPaytable] = useState(false);
  const [showAutoSessionModal, setShowAutoSessionModal] = useState(false);
  const [showSaveConfigModal, setShowSaveConfigModal] = useState(false);
  const [showLoadConfigModal, setShowLoadConfigModal] = useState(false);
  const [showRenameConfigModal, setShowRenameConfigModal] = useState(false);
  const [renameConfigIndex, setRenameConfigIndex] = useState(null);
  const [renameConfigName, setRenameConfigName] = useState('');



  // Keep creditRef in sync with credit state
  useEffect(() => {
    creditRef.current = credit;
  }, [credit]);

  const speedDelays = {
    'Slow': 250,
    'Med': 125,
    'Fast': 0
  };

useEffect(() => {
  if (autoSessionRemaining > 0 && !isAutoSession) {
    setShowContinueButton(true);
  }
}, [autoSessionRemaining, isAutoSession, setShowContinueButton]);


  const cycleSpeed = () => {
    const speeds = ['Slow', 'Med', 'Fast'];
    const currentIndex = speeds.indexOf(speed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    setSpeed(speeds[nextIndex]);
  };

  const [paytables, setPaytables] = useState(() => {
    const loaded = loadFromStorage('keno-paytables', defaultPaytables);
    let updated = { ...loaded };
    
    // Migrate 'standard' to '838' if it exists
    if (updated.standard && !updated['838']) {
      const { standard, ...rest } = updated;
      updated = { '838': standard, ...rest };
    }
    
    // Add '810' paytable if it doesn't exist
    if (!updated['810']) {
      updated['810'] = defaultPaytables['810'];
    }
    
    // Add '750' paytable if it doesn't exist
    if (!updated['750']) {
      updated['750'] = defaultPaytables['750'];
    }
    
    // Add '804' paytable if it doesn't exist
    if (!updated['804']) {
      updated['804'] = defaultPaytables['804'];
    }
    
    return updated;
  });

  // Migrate activePaytable from 'standard' to '838'
  useEffect(() => {
    if (activePaytable === 'standard') {
      setActivePaytable('838');
    }
  }, [activePaytable, setActivePaytable]);

  // Save to localStorage whenever these values change
  useEffect(() => {
    localStorage.setItem('keno-paytables', JSON.stringify(paytables));
  }, [paytables]);

  useEffect(() => {
    localStorage.setItem('keno-configurations', JSON.stringify(savedConfigurations));
  }, [savedConfigurations]);

  const handleSaveConfig = (name) => {
    const config = {
      name,
      timestamp: new Date().toISOString(),
      cardSelections,
      bets,
      activePaytable,
      denomination
    };
    setSavedConfigurations(prev => {
      // Check if name exists and replace it
      const existingIndex = prev.findIndex(c => c.name === name);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = config;
        return updated;
      }
      return [...prev, config];
    });
  };

  const handleLoadConfig = (config) => {
    setCardSelections(config.cardSelections);
    setBets(config.bets);
    setActivePaytable(config.activePaytable);
    setDenomination(config.denomination);
    setShowAllCards(true);
    setShowLoadConfigModal(false);
  };

  const handleDeleteConfig = (index) => {
    setSavedConfigurations(prev => prev.filter((_, i) => i !== index));
  };

  const handleRenameConfig = (index, currentName) => {
    setRenameConfigIndex(index);
    setRenameConfigName(currentName);
    setShowRenameConfigModal(true);
  };

  const handleRenameSubmit = (newName) => {
    if (renameConfigIndex !== null) {
      setSavedConfigurations(prev => {
        const updated = [...prev];
        // Check if overwriting another config
        const existingIndex = updated.findIndex(c => c.name === newName);
        if (existingIndex !== -1 && existingIndex !== renameConfigIndex) {
          // Remove the existing one being overwritten
          updated.splice(existingIndex, 1);
          // Adjust index if needed
          const actualIndex = existingIndex < renameConfigIndex ? renameConfigIndex - 1 : renameConfigIndex;
          updated[actualIndex] = { ...updated[actualIndex], name: newName };
        } else {
          updated[renameConfigIndex] = { ...updated[renameConfigIndex], name: newName };
        }
        return updated;
      });
    }
  };

  const handleExportConfigs = () => {
    const dataStr = JSON.stringify(savedConfigurations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `keno-configs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportConfigs = (imported) => {
    if (Array.isArray(imported)) {
      setSavedConfigurations(prev => {
        const updated = [...prev];
                
        imported.forEach(config => {
          const existingIndex = updated.findIndex(c => c.name === config.name);
          if (existingIndex !== -1) {
            // Replace existing config with same name
            updated[existingIndex] = config;
          } else {
            // Add new config
            updated.push(config);
          }
        });
        
        return updated;
      });
    }
  };


  const maxBetPerCard = 4;

  const toggleNumberSelection = (num) => {
    if (showAllCards || !activeCard || (bets[activeCard] || 0) === 0) {
      return;
    }
    
    // Check if card has been played (has any hit frequency data)
    const cardHasData = hitFrequencies[activeCard] && 
      Object.keys(hitFrequencies[activeCard])
        .filter(key => key !== 'totalWon')
        .some(key => hitFrequencies[activeCard][key] > 0);
    
    if (cardHasData) {
      // Card is locked - cannot change spots until bet is changed or session reset
      return;
    }
    
    setCardSelections(prev => {
      const current = prev[activeCard];
      const isSelected = current.includes(num);

      if (!isSelected && current.length >= 10) {
        return prev;
      }

      return {
        ...prev,
        [activeCard]: isSelected
          ? current.filter(n => n !== num)
          : [...current, num]
      };
    });
  };

  const clearActiveCard = () => {
    setCardSelections(prev => ({
      ...prev,
      [activeCard]: []
    }));
    setDrawnNumbers([]);
    setHits(prev => ({
      ...prev,
      [activeCard]: 0
    }));
    setPayouts(prev => ({
      ...prev,
      [activeCard]: 0
    }));
  };

  const clearAllCards = () => {
    setCardSelections(prev =>
      Object.keys(prev).reduce((acc, card) => {
        acc[card] = [];
        return acc;
      }, {})
    );
    setDrawnNumbers([]);
    setHits(cards.reduce((acc, card) => ({ ...acc, [card]: 0 }), {}));
    setPayouts(cards.reduce((acc, card) => ({ ...acc, [card]: 0 }), {}));
  };

  const resetResults = () => {
    setDrawnNumbers([]);
    setHits(cards.reduce((acc, card) => ({ ...acc, [card]: 0 }), {}));
    setPayouts(cards.reduce((acc, card) => ({ ...acc, [card]: 0 }), {}));
    setTotalWin(0);
  };

  const emptyBets = () => cards.reduce((acc, card) => ({ ...acc, [card]: 0 }), {});

  const handleBetOne = () => {
    if (showAllCards) {
      const allMaxed = cards.every(card => (bets[card] || 0) >= maxBetPerCard);
      if (allMaxed) {
        resetResults();
        setBets(emptyBets());
        setBetSequenceStarted(false);
        setBetCursor(0);
        // Clear hit frequencies for all cards
        setHitFrequencies(cards.reduce((acc, card) => {
          acc[card] = { 
            0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0,
            totalWon: 0 
          };
          return acc;
        }, {}));
        return;
      }

      resetResults();

      setBets(prev => {
        const base = betSequenceStarted ? { ...prev } : emptyBets();
        let idx = betSequenceStarted ? betCursor : 0;
        let attempts = 0;

        while (attempts < cards.length && base[cards[idx]] >= maxBetPerCard) {
          idx = (idx + 1) % cards.length;
          attempts += 1;
        }

        if (attempts >= cards.length) {
          return base;
        }

        base[cards[idx]] = Math.min(maxBetPerCard, base[cards[idx]] + 1);
        setBetCursor((idx + 1) % cards.length);
        
        // Clear hit frequencies for this card when bet changes
        setHitFrequencies(prevFreq => ({
          ...prevFreq,
          [cards[idx]]: { 
            0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0,
            totalWon: 0 
          }
        }));
        
        return base;
      });

      if (!betSequenceStarted) {
        setBetSequenceStarted(true);
      }

      return;
    }

    if (!activeCard) {
      return;
    }

    resetResults();
    
    // Clear hit frequencies for this card when bet changes
    setHitFrequencies(prev => ({
      ...prev,
      [activeCard]: { 
        0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0,
        totalWon: 0 
      }
    }));
    
    setBets(prev => {
      const current = prev[activeCard] || 0;
      const next = current >= maxBetPerCard ? 0 : current + 1;
      return {
        ...prev,
        [activeCard]: next
      };
    });
  };

  const handleBetAllCards = () => {
    resetResults();
    setBetSequenceStarted(false);
    setBetCursor(0);

    setBets(prev => {
      const allMaxed = cards.every(card => (prev[card] || 0) >= maxBetPerCard);
      if (allMaxed) {
        // Clear all hit frequencies when resetting all bets
        setHitFrequencies(cards.reduce((acc, card) => {
          acc[card] = { 
            0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0,
            totalWon: 0 
          };
          return acc;
        }, {}));
        return emptyBets();
      }

      // Clear hit frequencies for all cards when any bet changes
      setHitFrequencies(cards.reduce((acc, card) => {
        acc[card] = { 
          0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0,
          totalWon: 0 
        };
        return acc;
      }, {}));

      return cards.reduce((acc, card) => {
        const current = prev[card] || 0;
        acc[card] = Math.min(maxBetPerCard, current + 1);
        return acc;
      }, {});
    });
  };

  const handleMaxBet = () => {
    const allMaxed = cards.every(card => (bets[card] || 0) >= maxBetPerCard);
    if (allMaxed) {
      return;
    }

    resetResults();
    setBetSequenceStarted(false);
    setBetCursor(0);
    
    // Clear all hit frequencies when setting max bet
    setHitFrequencies(cards.reduce((acc, card) => {
      acc[card] = { 
        0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0,
        totalWon: 0 
      };
      return acc;
    }, {}));
    
    setBets(cards.reduce((acc, card) => ({ ...acc, [card]: maxBetPerCard }), {}));
  };

  const calculatePayouts = (hitCounts) => {
    const currentPaytable = paytables[activePaytable] || paytables['standard'];
    return cards.reduce((acc, card) => {
      const hitCount = hitCounts[card];
      const spotCount = cardSelections[card].length;
      const payout = (currentPaytable[spotCount.toString()] && currentPaytable[spotCount.toString()][hitCount]) || 0;
      acc[card] = payout;
      return acc;
    }, {});
  };

  const getRandomNumbers = (count) => {
    const nums = Array.from({ length: 80 }, (_, i) => i + 1);
    for (let i = nums.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [nums[i], nums[j]] = [nums[j], nums[i]];
    }
    return nums.slice(0, count);
  };

  const quickPickActiveCard = () => {
    if (!activeCard) {
      return;
    }
    const count = cardSelections[activeCard]?.length || 10;
    setCardSelections(prev => ({
      ...prev,
      [activeCard]: getRandomNumbers(count)
    }));
  };

  const quickPickAllCards = () => {
    setCardSelections(prev => {
      const cardsWithSelections = cards.filter(card => prev[card].length > 0);
      const hasNoSelections = cardsWithSelections.length === 0;
      const hasSingleSelectionSet = cardsWithSelections.length === 1;
      const singleCount = hasSingleSelectionSet ? prev[cardsWithSelections[0]].length : null;

      return cards.reduce((acc, card) => {
        let count = 0;

        if (hasNoSelections) {
          count = 10;
        } else if (hasSingleSelectionSet) {
          count = singleCount;
        } else {
          count = prev[card].length > 0 ? prev[card].length : 10;
        }

        acc[card] = count > 0 ? getRandomNumbers(count) : [];
        return acc;
      }, {});
    });
  };

  const handleQuickPick = () => {
    if (showAllCards) {
      quickPickAllCards();
    } else {
      quickPickActiveCard();
    }
  };

  const handleErase = () => {
    if (showAllCards) {
      clearAllCards();
    } else {
      clearActiveCard();
    }
  };

  const startAutoSession = (numGames, ignoreLimitCheck = false) => {
    // Prevent auto session if no bets or no cards marked
    const totalBetCredits = Object.values(bets).reduce((sum, bet) => sum + bet, 0);
    const hasMarkedCard = Object.keys(bets).some(card => (bets[card] || 0) > 0 && (cardSelections[card]?.length || 0) >= 2);
    if (totalBetCredits === 0 || !hasMarkedCard) {
      setShowAutoSessionModal(false);
      return;
    }
    // Cap numGames for free users
    if (!isPremium && !ignoreLimitCheck) {
      const remaining = FREE_GAME_LIMIT - sessionGamesPlayed;
      if (remaining <= 0) { setShowAutoSessionModal(false); return; }
      numGames = Math.min(numGames, remaining);
    }
    // Increment session ID to cancel any pending callbacks from previous session
    autoSessionIdRef.current += 1;
    const currentSessionId = autoSessionIdRef.current;
    // Set up new auto session (don't reset stats - let them accumulate)
    setIsAutoSession(true);
    setAutoSessionTotal(numGames);
    setAutoSessionRemaining(numGames);
    setShowAutoSessionModal(false);
    // Start the first game
    setTimeout(() => {
      if (autoSessionIdRef.current === currentSessionId) {
        startGame(true, currentSessionId);
      }
    }, 100);
  };

  const startGame = (isAutoGame = false, sessionId = null) => {
    // Handle case where event object is passed instead of boolean
    if (typeof isAutoGame === 'object') {
      isAutoGame = false;
    }
    // Initialize credit check to false for this execution
    creditCheckRef.current = false;
    // Prevent starting a new game if one is already running
    if (isGameRunningRef.current) {
      return;
    }
    // Block free users at the session game limit — show continue modal instead of silent block
    if (atSessionLimit) {
      if (isAutoGame) setIsAutoSession(false);
      pendingContinueAction.current = 'manual';
      setShowSessionLimitModal(true);
      return;
    }
    // If this is an auto game, verify the session ID matches
    if (isAutoGame && sessionId !== null && autoSessionIdRef.current !== sessionId) {
      return;
    }
    // Prevent starting if no bets or no cards marked
    const totalBetCredits = Object.values(bets).reduce((sum, bet) => sum + bet, 0);
    const hasMarkedCard = Object.keys(bets).some(card => (bets[card] || 0) > 0 && (cardSelections[card]?.length || 0) >= 2);
    if (totalBetCredits === 0 || !hasMarkedCard) {
      if (isAutoGame) {
        setIsAutoSession(false);
      }
      return;
    }
    const totalWager = totalBetCredits * denomination;
    // Prevent starting if any card with a bet has less than 2 numbers marked
    const invalidCard = Object.keys(bets).find(card => (bets[card] || 0) > 0 && (cardSelections[card]?.length || 0) < 2);
    if (invalidCard) {
      if (isAutoGame) {
        setIsAutoSession(false);
      }
      return;
    }
    // Check using the ref which has the current credit value
    if (creditRef.current < totalWager) {
      if (isAutoGame) {
        setIsAutoSession(false);
      }
      return;
    }
    // Deduct the credit
    setCredit(prev => prev - totalWager);
    
    // Mark game as running
    isGameRunningRef.current = true;
    
    setDrawnNumbers([]);
    setIsGameRunning(true);
    setTotalWin(0);
    
    // Update session stats - only increment counters, save win amount for later
    setSessionGamesPlayed(prev => prev + 1);
    setSessionTotalWagered(prev => prev + totalWager);
    
    const gameNumbers = getRandomNumbers(20);
    const delay = speedDelays[speed];
    
    if (delay === 0) {
      // Fast mode: draw all numbers at once
      const newHits = cards.reduce((acc, card) => ({ ...acc, [card]: 0 }), {});
      gameNumbers.forEach(num => {
        cards.forEach(card => {
          if (cardSelections[card].includes(num)) {
            newHits[card] += 1;
          }
        });
      });
      setHits(newHits);
      const newPayouts = calculatePayouts(newHits);
      setPayouts(newPayouts);
      setDrawnNumbers(gameNumbers);
      
      // Update hit frequencies
      setHitFrequencies(prev => {
        const updated = { ...prev };
        cards.forEach(card => {
          if (cardSelections[card].length > 0 && bets[card] > 0) {
            const hitCount = newHits[card];
            const payout = (newPayouts[card] || 0) * bets[card] * denomination;
            updated[card] = { 
              ...updated[card], 
              [hitCount]: updated[card][hitCount] + 1,
              totalWon: (updated[card].totalWon || 0) + payout
            };
          }
        });
        return updated;
      });
      
      const totalCredits = cards.reduce((sum, card) => {
        if (bets[card] > 0) {
          return sum + (newPayouts[card] || 0) * bets[card];
        }
        return sum;
      }, 0);
      const totalWinAmount = totalCredits * denomination;
      setTotalWin(totalCredits);
      
      setTimeout(() => {
        isGameRunningRef.current = false;
        setIsGameRunning(false);
        setCredit(prev => prev + totalCredits * denomination);
        setSessionTotalWon(prev => prev + totalWinAmount);
        
        // Handle auto session continuation
        if (isAutoGame && sessionId !== null) {
          setAutoSessionRemaining(prev => {
            const remaining = prev - 1;
            if (remaining > 0 && autoSessionIdRef.current === sessionId) {
              setTimeout(() => {
                if (autoSessionIdRef.current === sessionId) {
                  startGame(true, sessionId);
                }
              }, 200);
            } else {
              setIsAutoSession(false);
            }
            return remaining;
          });
        }
      }, 100);
    } else {
      // Draw numbers sequentially with delay
      let drawn = [];
      let index = 0;
      
      const drawNext = () => {
        if (index < gameNumbers.length) {
          drawn.push(gameNumbers[index]);
          setDrawnNumbers([...drawn]);
          
          // Calculate hits for all cards
          const newHits = cards.reduce((acc, card) => ({ ...acc, [card]: 0 }), {});
          drawn.forEach(num => {
            cards.forEach(card => {
              if (cardSelections[card].includes(num)) {
                newHits[card] += 1;
              }
            });
          });
          setHits(newHits);
          
          index++;
          
          if (index < gameNumbers.length) {
            setTimeout(drawNext, delay);
          } else {
            // All numbers drawn, calculate final payouts
            const newPayouts = calculatePayouts(newHits);
            setPayouts(newPayouts);
            
            // Update hit frequencies
            setHitFrequencies(prev => {
              const updated = { ...prev };
              cards.forEach(card => {
                if (cardSelections[card].length > 0 && bets[card] > 0) {
                  const hitCount = newHits[card];
                  const payout = (newPayouts[card] || 0) * bets[card] * denomination;
                  updated[card] = { 
                    ...updated[card], 
                    [hitCount]: updated[card][hitCount] + 1,
                    totalWon: (updated[card].totalWon || 0) + payout
                  };
                }
              });
              return updated;
            });
            
            const totalCredits = cards.reduce((sum, card) => {
              if (bets[card] > 0) {
                return sum + (newPayouts[card] || 0) * bets[card];
              }
              return sum;
            }, 0);
            const totalWinAmount = totalCredits * denomination;
            setTotalWin(totalCredits);
            
            setTimeout(() => {
              isGameRunningRef.current = false;
              setIsGameRunning(false);
              setCredit(prev => prev + totalCredits * denomination);
              setSessionTotalWon(prev => prev + totalWinAmount);
              
              // Handle auto session continuation
              if (isAutoGame && sessionId !== null) {
                setAutoSessionRemaining(prev => {
                  const remaining = prev - 1;
                  if (remaining > 0 && autoSessionIdRef.current === sessionId) {
                    setTimeout(() => {
                      if (autoSessionIdRef.current === sessionId) {
                        startGame(true, sessionId);
                      }
                    }, 500);
                  } else {
                    setIsAutoSession(false);
                  }
                  return remaining;
                });
              }
            }, 500);
          }
        }
      };
      
      drawNext();
    }
  };

  const handleEditOptimizerGrid = () => {
    setOptimizerGridLocked(false);
    setCanShuffle(false);
  };

  if (!isLoaded) return null;
  if (!isSignedIn) return <RedirectToSignIn />;

  return (
    <div className="app-container">
      <main className="main-layout">
        <LeftPanel 
          cardSelections={cardSelections} 
          hits={hits} 
          payouts={payouts} 
          bets={bets}
          totalWin={totalWin}
          onToggleShowAll={() => {
            setShowAllCards(!showAllCards);
            if (!showAllCards) {
              setActiveCard(null);
            }
            setBetSequenceStarted(false);
            setBetCursor(0);
          }}
          showAllCards={showAllCards}
          onErase={handleErase}
          eraseLabel={showAllCards ? 'Erase All' : 'Erase'}
          onQuickPick={handleQuickPick}
          quickPickLabel={showAllCards ? 'Quick Pick All' : 'Quick Pick'}
          activePaytable={activePaytable}
          activeCard={activeCard}
        />
        <div className="left-column">
          {toastVisible && (
            <div style={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#228b22',
              color: '#fff',
              padding: '0.6rem 1.2rem',
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              zIndex: 9999,
              boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}>
              {toastMsg}
            </div>
          )}
          <CardList
            activeCard={activeCard}
            onSelectCard={(card) => {
              setActiveCard(card);
              setShowAllCards(false);
            }}
            cardSelections={cardSelections}
          />

         {/* Keno board — shown when optimizer is off */}
         {!optimizerMode && (
           <div className="test-keno-boards">
             <KenoBoard
               activeCard={activeCard}
               cardSelections={cardSelections}
               onToggleNumber={toggleNumberSelection}
               showAllCards={showAllCards}
               drawnNumbers={drawnNumbers}
               bets={bets}
             />
           </div>
         )}

         {/* Optimizer mode — blue board + red controls side by side inside purple border */}
         {optimizerMode && (
           <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', border: '1px solid purple', borderRadius: '8px', padding: '0.5rem', background: '#222', boxSizing: 'border-box', minHeight: '395px' }}>
             {/* Inner row: blue + red side by side */}
             <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: '0.5rem' }}>

             {/* Blue: optimizer number selection grid */}
             <div style={{ flex: '0 0 auto', border: '1px solid blue', borderRadius: '8px', padding: '0.5rem', background: '#222' }}>
               <div style={{ marginBottom: '0.25rem', color: 'white'}}>
                 <span style={{display: 'flex',marginBottom: '.75rem', justifyContent: 'center', gap: '0.4rem'}}>
                   Select up to 20 numbers:
                   <span style={{ fontWeight: 'bold', color: optimizerNumbers.length >= 20 ? '#ff6b6b' : '#7fff7f' }}>
                     {optimizerNumbers.length}
                   </span>
                 </span>
               </div>
               <div style={{ background: '#008b8b',border: '2px solid white', display: 'grid', gridTemplateColumns: 'repeat(10, 2rem)', gap: '2px',padding: '2px' }}>
                 {Array.from({ length: 8 }).map((_, rowIdx) => (
                   Array.from({ length: 10 }).map((_, colIdx) => {
                     const num = rowIdx * 10 + colIdx + 1;
                     return (
                       <button
                         key={num}
                         onClick={() => handleOptimizerNumberToggle(num)}
                         disabled={optimizerGridLocked}
                         style={{
                           width: '2rem', height: '2rem', borderRadius: '5%', border: '1px solid #ccc',
                           background: optimizerNumbers.includes(num) ? 'blue' : '#fff',
                           color: optimizerNumbers.includes(num) ? '#fff' : '#000',
                           fontWeight: optimizerNumbers.includes(num) ? 'bold' : 'normal',
                           cursor: optimizerGridLocked ? 'not-allowed' : 'pointer',
                           outline: 'none',
                           opacity: optimizerGridLocked ? 0.6 : 1,
                         }}
                       >
                         {num}
                       </button>
                     );
                   })
                 ))}
               </div>
             </div>

             {/* Red: selectors, status message, and action buttons */}
             <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1 1 0', minWidth: 0, overflow: 'hidden', border: '1px solid red', borderRadius: '8px', padding: '0', background: '#222' }}>
               

               {/* Steppers */}
               <div style={{ color: 'white', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', border: '0px solid yellow', borderRadius: '8px', padding: '0.5rem', background: '#222' }}>
<span style={{ justifyContent: 'center', color: 'white', display: 'flex' }}>Select</span>
                 {/* Spots per card stepper (2–10) */}
                 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                     <button
                       onClick={() => setOptimizerSpots(s => Math.max(2, s - 1))}
                       style={{ width: '1.5rem', height: '1.5rem', background: '#444', color: '#fff', border: '1px solid #888', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                     >−</button>
                     <span style={{ minWidth: '1.5rem', textAlign: 'center', fontWeight: 'bold' }}>{optimizerSpots}</span>
                     <button
                       onClick={() => setOptimizerSpots(s => Math.min(10, s + 1))}
                       style={{ width: '1.5rem', height: '1.5rem', background: '#444', color: '#fff', border: '1px solid #888', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                     >+</button>
                   </div>
                   
                   <span style={{ fontSize: '.75rem', textAlign: 'center' }}>Spots per card</span>
                 </div>

                 {/* Number of cards stepper (1–20) */}
                 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                     <button
                       onClick={() => setOptimizerCardCount(c => Math.max(1, c - 1))}
                       style={{ width: '1.5rem', height: '1.5rem', background: '#444', color: '#fff', border: '1px solid #888', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                     >−</button>
                     <span style={{ minWidth: '1.5rem', textAlign: 'center', fontWeight: 'bold' }}>{optimizerCardCount}</span>
                     <button
                       onClick={() => setOptimizerCardCount(c => Math.min(20, c + 1))}
                       style={{ width: '1.5rem', height: '1.5rem', background: '#444', color: '#fff', border: '1px solid #888', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
                     >+</button>
                   </div>
                   <span style={{ fontSize: '.75rem', textAlign: 'center' }}># of Cards</span>
                 </div>

                 {(() => {
                   const possible = nCr(optimizerNumbers.length, optimizerSpots);
                   const show = optimizerNumbers.length >= optimizerSpots && optimizerCardCount > possible;
                   return (
                     <div style={{ fontSize: '0.7rem', color: '#ffcc00', textAlign: 'center', maxWidth: '8rem', lineHeight: 1.3, visibility: show ? 'visible' : 'hidden' }}>
                       Only {possible} {optimizerSpots}-spot combo{possible !== 1 ? 's' : ''} possible with {optimizerNumbers.length} numbers selected
                     </div>
                   );
                 })()}

                 <button
                   onClick={handleRunOptimizer}
                   disabled={optimizerNumbers.length < optimizerSpots}
                   title={optimizerNumbers.length < optimizerSpots ? `Select at least ${optimizerSpots} numbers to optimize` : ''}
                   style={{
                     width: '100%',
                     padding: '0.25rem .5rem',
                     background: optimizerNumbers.length < optimizerSpots ? '#888' : 'red',
                     color: '#fff',
                     border: 'none',
                     borderRadius: '4px',
                     fontWeight: 'bold',
                     fontSize: '.75rem',
                     cursor: optimizerNumbers.length < optimizerSpots ? 'not-allowed' : 'pointer',
                   }}
                 >
                   Optimize
                 </button>



               </div>

           <div style={{ color: 'white', display: 'flex', flexDirection: 'column', gap: '0.25rem', border: '0px solid yellow', borderRadius: '8px', padding: '0.5rem', background: '#222' }}>

               {/* Action buttons */}
               <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                 <button
                   onClick={handleEditOptimizerGrid}
                   disabled={!optimizerGridLocked}
                   style={{
                     width: '100%',
                     padding: '0.25rem .5rem',
                     background: optimizerGridLocked ? '#228b22' : '#888',
                     color: '#fff',
                     border: 'none',
                     borderRadius: '4px',
                     fontWeight: 'bold',
                     fontSize: '.75rem',
                     cursor: optimizerGridLocked ? 'pointer' : 'not-allowed',
                   }}
                 >
                   Edit Selection
                 </button>
                 <button
                   onClick={() => setOptimizerNumbers([])}
                   disabled={optimizerGridLocked}
                   style={{
                     padding: '0.25rem .5rem',
                     background: optimizerGridLocked ? '#888' : '#b8860b',
                     color: '#fff',
                     border: 'none',
                     borderRadius: '4px',
                     fontWeight: 'bold',
                     fontSize: '.75rem',
                     cursor: optimizerGridLocked ? 'not-allowed' : 'pointer',
                   }}
                 >
                   Clear Selection
                 </button>
                 <button
                   onClick={handleShuffleActiveCard}
                   disabled={!(canShuffle && activeCard && !showAllCards)}
                   style={{
                     display: 'none',
                     padding: '0.25rem .5rem',
                     background: canShuffle && activeCard && !showAllCards ? '#228b22' : '#888',
                     color: '#fff',
                     border: 'none',
                     borderRadius: '4px',
                     fontWeight: 'bold',
                     fontSize: '.75rem',
                     cursor: canShuffle && activeCard && !showAllCards ? 'pointer' : 'not-allowed',
                   }}
                 >
                   Shuffle Card
                 </button>

               </div>
</div>
             </div>
             </div>{/* end inner row */}

             {/* Orange: status message spanning full width */}
             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: '0px solid orange', borderRadius: '4px', padding: '2px 0.5rem',marginTop: '0.25rem',marginBottom: '0.25rem', background: '#222', minHeight: '1.4rem' }}>
               {optimizerStatusMsg && (
                 <div style={{ color: '#fff', fontSize: '0.95rem' }}>
                   {optimizerStatusMsg}
                 </div>
               )}
             </div>

           </div>
         )}

        <div className="optimizer-control-panel" >

          <label style={{ fontSize: '0.875rem',border: '0px solid white', borderRadius: '4px', background: 'green', fontWeight: 'bold', color: 'white', marginBottom: '0.0rem', padding: '0',paddingRight: '6px' }}>
            <input
              type="checkbox"
              checked={optimizerMode}
              onChange={e => setOptimizerMode(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            Show Optimizer
          </label>

          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button
              onClick={handleSaveConfigClick}
              style={{
                padding: '2px 8px',
                height: '22px',
                fontSize: '0.75rem',
                fontWeight: '600',
                background: '#28a745',
                color: '#fff',
                border: '0px solid white',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Save Config
            </button>
            <button
              onClick={() => setShowLoadConfigModal(true)}
              style={{
                padding: '2px 8px',
                height: '22px',
                fontSize: '0.75rem',
                fontWeight: '600',
                background: '#007bff',
                color: '#fff',
                border: '0px solid white',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Load Config
            </button>
          </div>

        </div>

          <div style={{ marginTop: '0.125rem' }}>
          <RightPanel 
            totalWin={totalWin} 
            denomination={denomination} 
            bets={bets} 
            credit={credit}
            showContinueButton={showContinueButton}
            cardSelections={cardSelections}
            allowBackgroundSessions={allowBackgroundSessions}
            setAllowBackgroundSessions={setAllowBackgroundSessions}
            onResetSession={() => {
              setSessionGamesPlayed(0);
              setSessionTotalWon(0);
              setSessionTotalWagered(0);
              // Do NOT reset bets here
              setHitFrequencies(
                cards.reduce((acc, card) => {
                  acc[card] = { 
                    0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0,
                    totalWon: 0 
                  };
                  return acc;
                }, {})
              );
              setIsAutoSession(false);
              setAutoSessionRemaining(0);
              setShowContinueButton(false);
            }}
            onStartAutoSession={() => {
              if (atSessionLimit) {
                pendingContinueAction.current = 'preAutoSession';
                setShowSessionLimitModal(true);
              } else {
                setShowAutoSessionModal(true);
              }
            }}
            onStopAutoSession={() => {
              autoSessionIdRef.current += 1; // Invalidate current session
              setIsAutoSession(false);
              // Do NOT reset autoSessionRemaining or autoSessionTotal, so session can be continued
            }}
            onContinueAutoSession={() => {
              // Resume auto session: set isAutoSession true and start game with current session id
              setIsAutoSession(true);
              startGame(true, autoSessionIdRef.current);
            }}
            sessionGamesPlayed={sessionGamesPlayed}
            sessionTotalWon={sessionTotalWon}
            sessionTotalWagered={sessionTotalWagered}
            isAutoSession={isAutoSession}
            autoSessionRemaining={autoSessionRemaining}
            autoSessionTotal={autoSessionTotal}
            isPremium={isPremium}
            freeGameLimit={FREE_GAME_LIMIT}
          />
          </div>
        </div>{/* end left-column */}
        <div className="right-column">
          <AnalysisPanel 
            hitFrequencies={hitFrequencies}
            cardSelections={cardSelections}
            bets={bets}
            denomination={denomination}
            selectedSpotSize={selectedSpotSize}
            paytable={paytables}
            activePaytable={activePaytable}
          />
          <AnalysisSummary 
            hitFrequencies={hitFrequencies}
            cardSelections={cardSelections}
            bets={bets}
            denomination={denomination}
            selectedSpotSize={selectedSpotSize}
            onSelectSpotSize={spot => setSelectedSpotSize(spot === selectedSpotSize ? null : spot)}
            paytable={paytables}
            activePaytable={activePaytable}
          />
        </div>
      </main>
      <SessionPanel
        onStart={startGame}
        isGameRunning={isGameRunning}
        onPays={() => setShowPayTable(true)}
        onBetOne={handleBetOne}
        onBetAllCards={handleBetAllCards}
        onMaxBet={handleMaxBet}
        denomination={denomination}
        onDenominationClick={() => setShowDenomModal(true)}
        speed={speed}
        onSpeedClick={cycleSpeed}
        onAddCredit={handleAddCredit}
        creditCapMsg={showCreditCapMsg}
        atSessionLimit={atSessionLimit}
        isPremium={isPremium}
        freeGameLimit={FREE_GAME_LIMIT}
        onCashOut={() => setCredit(0)}
        onSaveConfig={handleSaveConfigClick}
        onLoadConfig={() => setShowLoadConfigModal(true)}
        credit={credit}
        bets={bets}
        cardSelections={cardSelections}
      />
      <PayTableModal
        isOpen={showPayTable}
        onClose={() => {
          setShowPayTable(false);
          setIsCreatingNewPaytable(false);
        }}
        paytable={paytables[activePaytable]}
        onSave={(updatedPaytable, newName) => {
          const trimmedName = newName.trim();
          if (trimmedName && trimmedName !== activePaytable) {
            // Rename: remove old key, add new key
            setPaytables(prev => {
              const updated = { ...prev };
              if (!isCreatingNewPaytable) {
                delete updated[activePaytable];
              }
              updated[trimmedName] = updatedPaytable;
              return updated;
            });
            setActivePaytable(trimmedName);
          } else {
            // Just update values, keep same name
            setPaytables(prev => ({
              ...prev,
              [activePaytable]: updatedPaytable
            }));
          }
          setIsCreatingNewPaytable(false);
        }}
        paytableNames={Object.keys(paytables)}
        activePaytable={activePaytable}
        onPaytableClick={() => {
          setShowPayTable(false);
          setShowPaytableModal(true);
        }}
        isNewPaytable={isCreatingNewPaytable}
        defaultPaytables={defaultPaytables}
        isPremium={isPremium}
      />
      <DenominationModal
        isOpen={showDenomModal}
        onClose={() => setShowDenomModal(false)}
        currentDenomination={denomination}
        onSelectDenomination={setDenomination}
      />
      <PaytableSelector
        isOpen={showPaytableModal}
        onClose={() => setShowPaytableModal(false)}
        paytableNames={Object.keys(paytables)}
        currentPaytable={activePaytable}
        onSelectPaytable={(paytableName) => {
          setActivePaytable(paytableName);
          setShowPaytableModal(false);
          setShowPayTable(true);
        }}
        onAddNew={() => {
          setIsCreatingNewPaytable(true);
          setShowPaytableModal(false);
          setShowPayTable(true);
        }}
        isPremium={isPremium}
      />
      {showSessionLimitModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: '#fff', padding: '28px 28px 22px',
            borderRadius: '10px', boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            minWidth: '320px', maxWidth: '400px', textAlign: 'center'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '8px' }}>25-Game Limit Reached</h2>
            <p style={{ color: '#555', marginBottom: '20px', lineHeight: 1.5 }}>
              Free accounts are limited to 25-game sessions. Clicking <strong>Continue</strong> will reset session statistics and game counter.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={handleContinueSession}
                style={{
                  padding: '10px 16px', backgroundColor: '#28a745', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '15px', fontWeight: '700'
                }}
              >
                Continue
              </button>
              <button
                onClick={() => { setShowSessionLimitModal(false); window.location.href = '/pricing'; }}
                style={{
                  padding: '10px 16px', backgroundColor: '#ffd600', color: '#1a1a1a',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '700'
                }}
              >
                ★ Upgrade to Premium — No Limits
              </button>
              <button
                onClick={() => { setShowSessionLimitModal(false); pendingContinueAction.current = null; }}
                style={{
                  padding: '8px 16px', backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showAutoSessionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            minWidth: '300px'
          }}>
            <h2 style={{ marginTop: 0 }}>Auto Session</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: isPremium ? '16px' : '8px' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Enter number of games to play:</span>
              <input
                type="number"
                min="1"
                max={isPremium ? undefined : FREE_GAME_LIMIT - sessionGamesPlayed}
                defaultValue={isPremium ? 100 : FREE_GAME_LIMIT - sessionGamesPlayed}
                id="autoSessionGames"
                style={{
                  width: '80px',
                  padding: '8px',
                  fontSize: '16px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const numGames = parseInt(e.target.value);
                    if (numGames > 0) {
                      startAutoSession(numGames);
                    }
                  }
                }}
              />
            </div>
            {!isPremium && (
              <p style={{ fontSize: '0.82rem', color: '#888', marginTop: '0', marginBottom: '16px' }}>
                Free plan: up to {FREE_GAME_LIMIT - sessionGamesPlayed} game{FREE_GAME_LIMIT - sessionGamesPlayed !== 1 ? 's' : ''} remaining this session.
              </p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAutoSessionModal(false)} style={{
                padding: '8px 16px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                Cancel
              </button>
              <button onClick={() => {
                const numGames = parseInt(document.getElementById('autoSessionGames').value);
                if (numGames > 0) {
                  startAutoSession(numGames);
                }
              }} style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfigLimitModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: '#fff', padding: '28px 28px 22px',
            borderRadius: '10px', boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            minWidth: '320px', maxWidth: '400px', textAlign: 'center'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '8px' }}>Config Limit Reached</h2>
            <p style={{ color: '#555', marginBottom: '20px', lineHeight: 1.5 }}>
              Free accounts can save <strong>1 configuration</strong>. Upgrade to Premium to save unlimited configurations.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => { setShowConfigLimitModal(false); window.location.href = '/pricing'; }}
                style={{
                  padding: '10px 16px', backgroundColor: '#ffd600', color: '#1a1a1a',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '700'
                }}
              >
                ★ Upgrade to Premium
              </button>
              <button
                onClick={() => setShowConfigLimitModal(false)}
                style={{
                  padding: '8px 16px', backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showSaveConfigModal && (
        <SaveConfigModal 
          onClose={() => setShowSaveConfigModal(false)}
          onSave={handleSaveConfig}
          existingNames={savedConfigurations.map(c => c.name)}
        />
      )}
      {showLoadConfigModal && (
        <LoadConfigModal 
          onClose={() => setShowLoadConfigModal(false)}
          onLoad={handleLoadConfig}
          onDelete={handleDeleteConfig}
          onRename={handleRenameConfig}
          onExport={handleExportConfigs}
          onImport={handleImportConfigs}
          configurations={savedConfigurations}
          isPremium={isPremium}
        />
      )}
      {showRenameConfigModal && (
        <RenameConfigModal 
          onClose={() => {
            setShowRenameConfigModal(false);
            setRenameConfigIndex(null);
            setRenameConfigName('');
          }}
          onRename={handleRenameSubmit}
          currentName={renameConfigName}
          existingNames={savedConfigurations.map(c => c.name)}
        />
      )}
    </div>
  );
}

export default Analyzer;
